import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DeleteOutlined, DragIndicator } from '@mui/icons-material';
import { Autocomplete, Button, Grid, IconButton, Stack, TextField as MuiTextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { contractFormText } from '../../constants/contracts';
import { useConfig } from '../../hooks/useConfig';
import { EMPTY_KPI_OPTIONS, KpiOptions, normalizeKpiOptionsResponse } from '../../util/kpiOptions';
import { getRequestJSON } from '../../util/services';
import FormSection from '../forms/FormSection';

interface SortableKpiRowProps {
  id: string;
  index: number;
  kpiList: string[];
  providerList: string[];
  isLoading: boolean;
  watch: any;
  remove: (index: number) => void;
  getGoalLabel: (index: number) => string;
  setValue: any;
  control: any;
}

function SortableKpiRow({ id, index, kpiList, providerList, isLoading, watch, remove, getGoalLabel, setValue, control }: SortableKpiRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Grid container spacing={2} ref={setNodeRef} style={style} alignItems="center">
      <Grid 
        item 
        xs={12} 
        sm={1} 
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'grab', pr: 0 }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          aria-label="Reorder KPI"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...attributes}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...listeners}
        >
          <DragIndicator fontSize="medium" />
        </div>
      </Grid>
      
      <Grid item xs={12} sm={4} sx={{ pl: 1 }}>
        <Controller
          name={`contractKpis.${index}.kpiName`}
          control={control}
          rules={{ required: 'KPI is required' }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              value={value || null}
              onChange={(_, v) => onChange((v || '').trim())}
              options={kpiList}
              disabled={isLoading}
              freeSolo={false}
              loading={isLoading}
              openOnFocus
              autoHighlight
              getOptionLabel={(o) => (typeof o === 'string' ? o : '')}
              isOptionEqualToValue={(o, v) => o === v}
              renderInput={(params) => {
                const { InputLabelProps, InputProps, ...other } = params;
                return (
                  <MuiTextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...other}
                    InputLabelProps={InputLabelProps}
                    InputProps={InputProps}
                    fullWidth
                    variant="outlined"
                    label={`${contractFormText.kpiSection.kpiLabel} *`}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={3.5}>
        <Controller
          name={`contractKpis.${index}.provider`}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              value={value || null}
              onChange={(_, v) => onChange((v || '').trim())}
              options={providerList}
              disabled={isLoading}
              freeSolo={false}
              loading={isLoading}
              openOnFocus
              autoHighlight
              getOptionLabel={(o) => (typeof o === 'string' ? o : '')}
              isOptionEqualToValue={(o, v) => o === v}
              renderInput={(params) => {
                const { InputLabelProps, InputProps, ...other } = params;
                return (
                  <MuiTextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...other}
                    InputLabelProps={InputLabelProps}
                    InputProps={InputProps}
                    fullWidth
                    variant="outlined"
                    label={contractFormText.kpiSection.providerLabel}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={2.5}>
        <Controller
          name={`contractKpis.${index}.goalValue`}
          control={control}
          rules={{ 
            required: { value: true, message: 'Goal is required' },
            min: { value: 0, message: 'Goal must be >= 0' },
            validate: (v) => v === '' || !Number.isNaN(Number(v)) || 'Goal must be a number'
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <MuiTextField
              value={value || ''}
              onChange={(event) => onChange(event.target.value)}
              type="number"
              fullWidth
              variant="outlined"
              placeholder={getGoalLabel(index)}
              error={!!error}
              helperText={error?.message}
              inputProps={{ step: 'any', min: 0 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconButton 
          onClick={() => {
            const kpi = watch(`contractKpis.${index}`);
            if (kpi?.id) {
              setValue(`contractKpis.${index}._destroy`, true, { shouldDirty: true });
            } else {
              remove(index);
            }
          }}
          aria-label={`Delete KPI ${index + 1}`}
          color="error"
        >
          <DeleteOutlined />
        </IconButton>
      </Grid>
    </Grid>
  );
}

function ContractKpiSection() {
  const { control, watch, setValue, formState: { errors } } = useFormContext();
  const { apiHost } = useConfig();
  const [kpiOptions, setKpiOptions] = useState<KpiOptions>(EMPTY_KPI_OPTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const inputRef = useRef(null);

  // Memoize lists to cut rerenders while typing
  const kpiList = React.useMemo(() => Array.isArray(kpiOptions?.kpis) ? kpiOptions.kpis : [], [kpiOptions]);
  const providerList = React.useMemo(() => Array.isArray(kpiOptions?.providers) ? kpiOptions.providers : [], [kpiOptions]);

  const {
    fields: kpiFields,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'contractKpis',
  });

  useEffect(() => {
    const fetchKpiOptions = async () => {
      try {
        setIsLoading(true);

        // Single source of truth – contractId is irrelevant
        const resp = await getRequestJSON(apiHost, '/admin/contracts/kpi_options');
        setKpiOptions(normalizeKpiOptionsResponse(resp));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching KPI options:', error);
        setKpiOptions(EMPTY_KPI_OPTIONS);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKpiOptions();
  }, [apiHost]);

  const handleAddKpi = () => {
    append({ 
      kpiName: '', 
      provider: '', 
      goalValue: '', 
      goalFormat: 'number',
      position: kpiFields.length
    }, { shouldFocus: true });
  };

  // Watch for KPI name changes to auto-set goal format and default values
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && name.startsWith('contractKpis.') && name.endsWith('.kpiName')) {
        const index = parseInt(name.split('.')[1], 10);
        const kpiName = value.contractKpis?.[index]?.kpiName;

        const formatMap = kpiOptions.kpi_goal_formats || {};
        const defaultMap = kpiOptions.kpi_default_values || {};

        if (kpiName && formatMap[kpiName]) {
          setValue(
            `contractKpis.${index}.goalFormat`,
            formatMap[kpiName],
            { shouldDirty: true, shouldValidate: true }
          );

          const defaultValue = defaultMap[kpiName];
          if (defaultValue !== undefined) {
            const currentGoalValue = value.contractKpis?.[index]?.goalValue;
            if (currentGoalValue === '' || currentGoalValue == null) {
              setValue(
                `contractKpis.${index}.goalValue`,
                defaultValue.toString(),
                { shouldDirty: true, shouldValidate: true }
              );
            }
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, kpiOptions]);

  const getGoalLabel = (index: number) => {
    const format = watch(`contractKpis.${index}.goalFormat`);
    if (format === 'percent') return `${contractFormText.kpiSection.goalLabel} (%)`;
    if (format === 'dollars') return `${contractFormText.kpiSection.goalLabel} ($)`;
    return contractFormText.kpiSection.goalLabel;
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: { active: any; over: any }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = kpiFields.findIndex((field) => field.id === active.id);
    const newIndex = kpiFields.findIndex((field) => field.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    move(oldIndex, newIndex);

    // Keep position in sync after move
    const currentKpis = watch('contractKpis') || [];
    currentKpis.forEach((_, i) => {
      setValue(`contractKpis.${i}.position`, i, { shouldDirty: true });
    });

    setValue('contractKpis', [...currentKpis], { shouldDirty: true });
  };

  useEffect(() => {
    if (errors && inputRef.current) {
      inputRef.current.focus();
    }
  }, [errors]);

  return (
    <FormSection title={contractFormText.sectionTitles.kpiSettings}>
      <Stack p={2} spacing={2} width={1}>
        <Stack direction="row" width={1} justifyContent="flex-start" alignItems="center" spacing={2}>
          <Typography variant="body2" color="text.primary">
            KPIs ({kpiFields.length})
          </Typography>
          {kpiFields.length === 0 && (
            <Button 
              onClick={handleAddKpi} 
              aria-label="Add New KPI"
              size="small"
              variant="outlined"
              disabled={isLoading}
            >
              {contractFormText.kpiSection.addButton}
            </Button>
          )}
        </Stack>

        {kpiFields.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {contractFormText.kpiSection.emptyState}
          </Typography>
        )}

        {errors.contractKpis && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }} ref={inputRef} tabIndex={-1}>
            {errors.contractKpis.message as string}
          </Typography>
        )}

        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={kpiFields.map(f => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={2}>
              {kpiFields
                .map((field, index) => ({ field, index }))
                .filter(({ index }) => !watch(`contractKpis.${index}._destroy`))
                .map(({ field, index }) => (
                  <SortableKpiRow
                    key={field.id}
                    id={field.id}
                    index={index}
                    kpiList={kpiList}
                    providerList={providerList}
                    isLoading={isLoading}
                    watch={watch}
                    remove={remove}
                    getGoalLabel={getGoalLabel}
                    setValue={setValue}
                    control={control}
                  />
                ))}
            </Stack>
          </SortableContext>
        </DndContext>

        {kpiFields.length > 0 && (
          <Button 
            onClick={handleAddKpi} 
            aria-label="Add New KPI"
            size="small"
            variant="outlined"
            disabled={isLoading}
            sx={{ alignSelf: 'flex-start' }}
          >
            {contractFormText.kpiSection.addButton}
          </Button>
        )}
      </Stack>
    </FormSection>
  );
}

export default ContractKpiSection;
